import { View, Accordion, Disclosure, DisclosureTitle, DisclosurePanel, Flex, TextField, Heading, Divider, Button, Link, ActionButton, Form, TextArea } from '@adobe/react-spectrum';
import React, { useEffect, useRef, useState } from 'react';
import { Check, ExclamationTriangle } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { Item } from "../../../../../services/soap/dataObjects/Item";
import ItemTypeDropDown from '../formComponents.tsx/ItemTypeDropDown';
import ItemStatusDropDown from '../formComponents.tsx/ItemStatusDropDown';
import ItemCategoryDropDown from '../formComponents.tsx/ItemCategoryDropDown';
import ItemUnitsDropDown from '../formComponents.tsx/ItemUnitsDropDown';
import { verify } from 'crypto';
import ManufacturerDroDown from '../formComponents.tsx/ManufacturerDropDown';
import DynamicProductTypeFields from '../formComponents.tsx/dynamicProductTypeFields';
import { useViewInfo } from '../../../../../hooks/UseViewInfo';

// Separate component sections
export const ProductDetailSection: React.FC<{
    data: Item;
    isEdit?: boolean
    onChange: (data: Item) => void;
    validationErrors?: any
    expandedSectionKey?: any
    setExpandedSectionKey: (data: any) => void;
    setValidationErrors: (data: any) => void;
    fieldConfig?: any
    validationStatus?: any
    setValidationStatus: (data: any) => void;
}> = ({ data = { name: '', type: '' }, isEdit, onChange, validationErrors, expandedSectionKey, setExpandedSectionKey,
    setValidationErrors,
    fieldConfig,
    validationStatus,
    setValidationStatus }) => {
        const [isConfirmEnabled, setIsConfirmEnabled] = useState(false);
        const [isSectionRequired, setIsSectionRequired] = useState(true);
        const [isCategoryRequired, setIsCategoryRequired] = useState(false);
        const { isMobile } = useViewInfo();
        const inputRef = useRef<any>(null);

        useEffect(() => {
            if (expandedSectionKey?.has?.("detailSection")) {
                setTimeout(() => {
                    inputRef.current?.focus();
                }, 200);
            }
        }, [expandedSectionKey]);

        const handleSectionExpandedChange = () => {
            setExpandedSectionKey((prevKey: any) => prevKey.has("detailSection") ? new Set() : new Set(["detailSection"]));
        };

        const handleConfirm = () => {
            const errors: any = {};
            const name = data?.name?.trim?.() || '';
            const type = data?.type?.trim?.() || '';
            const status = data?.status?.trim?.() || '';
            const category = data?.componentCategory?.trim?.() || '';
            const units = data?.units?.trim?.() || '';
            const producer = data?.manufacturer?.trim?.() || '';
            console.log('error data',data);
            if (name === '') {
                errors.nameError = _messages('product_name_required');
            }
            if (type === '') {
                errors.typeError = _messages('item_type_required');
            }
            if (status === '') {
                errors.statusError = _messages('item_status_required');
            }
            if (category === '' && isCategoryRequired) {
                errors.categoryError = _messages('item_category_required');
            }
            if (units === '') {
                errors.unitsError = _messages('item_unit_required');
            }
            if (producer === '') {
                errors.producerError = _messages('item_producer_required');
            }
            // setIsSectionRequired(name === '');

            if (Object.keys(errors).length > 0) {
                setValidationErrors(errors);
                setValidationStatus((prev: any) => ({ ...prev, ['detailSection']: false }));
                return;
            }
            setValidationErrors({});
            setValidationStatus((prev: any) => ({ ...prev, ['detailSection']: true }));
            setExpandedSectionKey(new Set(['pricesSection']));
        };

        useEffect(() => {
            if (isEdit) {
                verifyRequiredFields();
            }
        }, [isEdit, data]);

        const verifyRequiredFields = () => {
            const errors: any = {};
            const name = data?.name?.trim?.() || '';
            const type = data?.type?.trim?.() || '';
            const status = data?.status?.trim?.() || '';
            const category = data?.componentCategory?.trim?.() || '';
            const units = data?.units?.trim?.() || '';
            const producer = data?.manufacturer?.trim?.() || '';

            if (name === '') {
                errors.nameError = '';
            }
            if (type === '') {
                errors.typeError = '';
            }
            if (status === '') {
                errors.statusError = '';
            }
            if (category === '' && isCategoryRequired) {
                errors.categoryError = '';
            }
            if (units === '') {
                errors.unitsError = '';
            }
            if (producer === '') {
                errors.producerError = '';
            }

            if (Object.keys(errors).length > 0) {
                setValidationStatus((prev: any) => ({ ...prev, ['detailSection']: false }));
                return;
            }
            setValidationStatus((prev: any) => ({ ...prev, ['detailSection']: true }));
        }

        const { t } = useTranslation('products');
        const _t = (key: string) => t(`addNewProduct.${key}`, { ns: 'products' });
        const _tt = (key: string) => t(`product_common.${key}`, { ns: 'products' });
        const _messages = (key: string) => t(`product_messages.${key}`, { ns: 'products' });
        const [producttype, setProductType] = useState<any>(null);
        return (
            <Accordion expandedKeys={expandedSectionKey} isQuiet onExpandedChange={() => handleSectionExpandedChange()}>
                <Disclosure id={"detailSection"}>
                    <DisclosureTitle>
                        <Flex justifyContent="space-between" alignItems="center" width="100%">
                            <View><span>{_t(`detailSection`)}</span><span style={{ color: '#B0BEC5', fontFamily: 'Adobe Clean' }}>{isSectionRequired && ` (${_tt('required')})`}</span></View>
                            {validationStatus.detailSection ? <Check size={20} color="#1976D2" /> : <ExclamationTriangle size={20} color="#FF9800" />}
                        </Flex>
                    </DisclosureTitle>
                    <DisclosurePanel>
                        <Divider size='M' UNSAFE_style={{ marginBottom: 5 }} />
                        <Flex direction={{ base: "column", L: 'column' }} gap="size-100" width="100%">
                            <Flex direction={{ base: "column", L: 'row' }} gap="size-100" width="100%">
                                <TextField
                                    label={_t("name")}
                                    ref={inputRef}
                                    aria-label={_t('name')}
                                    value={data?.name ?? ''}
                                    onChange={(name) => {
                                        onChange({ ...data, name });
                                        if (validationErrors?.nameError && validationErrors?.nameError !== '') {
                                            setValidationErrors((prev: any) => ({
                                                ...prev,
                                                nameError: undefined,
                                            }));
                                        }
                                        verifyRequiredFields();
                                    }}
                                    width={{ L: "50%", base: '100%' }}
                                    isRequired
                                    errorMessage={validationErrors.nameError}
                                    validationState={validationErrors.nameError ? validationErrors.nameError !== '' ? 'invalid' : 'valid' : undefined}
                                />
                                <ItemTypeDropDown
                                    isEdit={isEdit}
                                    selectedType={data?.type ?? ''}
                                    isRequired={true}
                                    errorMessage={validationErrors?.typeError}
                                    validationState={validationErrors.typeError ? validationErrors?.typeError !== '' ? 'invalid' : 'valid' : undefined}
                                    onChange={(typeData) => {
                                        onChange({ ...data, type: typeData && typeData.id > 0 ? typeData?.id.toString() : '', measureStock: typeData?.measureStock });
                                        if (validationErrors?.typeError && validationErrors?.typeError !== '' && typeData?.id > 0) {
                                            setValidationErrors((prev: any) => ({
                                                ...prev,
                                                typeError: undefined,
                                            }));
                                        }
                                        verifyRequiredFields();
                                    }}
                                    setProductType={setProductType} />
                            </Flex>
                            <Flex direction={{ base: "column", L: 'row' }} gap="size-100" width="100%">
                                <TextField
                                    label={_t("refId")}
                                    aria-label={_t('refId')}
                                    value={data?.refId ?? ''}
                                    onChange={(refId) => {
                                        onChange({ ...data, refId });
                                    }}
                                    width={{ base: '100%', L: "50%" }}
                                />
                                <ItemStatusDropDown selectedStatus={data.status || ''}
                                    onChange={(statusData) => {
                                        onChange({ ...data, status: statusData && statusData.id !== '' ? statusData?.id.toString() : '' });
                                        if (validationErrors?.statusError && validationErrors?.statusError !== '' && statusData?.id !== '') {
                                            setValidationErrors((prev: any) => ({
                                                ...prev,
                                                statusError: undefined,
                                            }));
                                        }
                                        verifyRequiredFields();
                                    }}
                                    isEdit={isEdit}
                                    isRequired={true}
                                    errorMessage={validationErrors?.statusError}
                                    validationState={validationErrors.statusError ? validationErrors?.statusError !== '' ? 'invalid' : 'valid' : undefined}
                                />
                            </Flex>
                            <Flex direction={{ base: "column", L: 'row' }} gap="size-100" width="100%">
                                <ItemCategoryDropDown
                                    selectedCategory={data.componentCategory || ''}
                                    onChange={(category) => {
                                        onChange({ ...data, componentCategory: category && category.id !== '' ? category?.id.toString() : '' });
                                        if (validationErrors?.categoryError && validationErrors?.categoryError !== '') {
                                            setValidationErrors((prev: any) => ({
                                                ...prev,
                                                categoryError: undefined,
                                            }));
                                        }
                                        verifyRequiredFields();
                                    }}
                                    isEdit={isEdit}
                                    isCategoryRequired={isCategoryRequired}
                                    setIsCategoryRequired={setIsCategoryRequired}
                                    isRequired={true}
                                    width={isMobile ? '100%' : '50%'}
                                    errorMessage={validationErrors?.categoryError}
                                    validationState={validationErrors.categoryError ? validationErrors?.categoryError !== '' ? 'invalid' : 'valid' : undefined}
                                />
                                <ManufacturerDroDown
                                    selectedProducer={data.manufacturer || ''}
                                    producerName={data.manufacturerName || ""}
                                    onChange={(producer) => {
                                        onChange({ ...data, manufacturer: producer?.id });
                                        if (validationErrors?.producerError && validationErrors?.producerError !== '' && producer?.id) {
                                            setValidationErrors((prev: any) => ({
                                                ...prev,
                                                producerError: undefined,
                                            }));
                                        }
                                        verifyRequiredFields();
                                    }}
                                    isEdit={isEdit}
                                    isAdd={!isEdit}
                                    isRequired={true}
                                    errorMessage={validationErrors?.producerError}
                                    validationState={validationErrors.producerError ? validationErrors?.producerError !== '' ? 'invalid' : 'valid' : undefined}
                                />
                            </Flex>
                            <Flex direction={{ base: "column", L: 'row' }} gap="size-100" width="100%">
                                <ItemUnitsDropDown
                                    selectedUnit={data.units || ''}
                                    onChange={(unit) => {
                                        onChange({ ...data, units: unit.id });
                                        if (validationErrors?.unitsError && validationErrors?.unitsError !== '') {
                                            setValidationErrors((prev: any) => ({
                                                ...prev,
                                                unitsError: undefined,
                                            }));
                                        }
                                        verifyRequiredFields();
                                    }}
                                    isEdit={isEdit}
                                    isRequired={true}
                                    errorMessage={validationErrors?.unitsError}
                                    validationState={validationErrors.unitsError ? validationErrors?.unitsError !== '' ? 'invalid' : 'valid' : undefined}
                                />
                            </Flex>
                            <Flex direction={{ base: "column", L: 'row' }} gap="size-100" width="100%">
                                <TextArea
                                    label={_t('note')}
                                    value={data.note || ''}
                                    onChange={(note) => {
                                        onChange({ ...data, note });
                                    }}
                                    width={"100%"}
                                ></TextArea>
                            </Flex>
                            <Flex direction={{ base: "column", L: 'row' }} gap="size-100" width="100%">
                                {producttype &&
                                    <DynamicProductTypeFields config={producttype} data={data} onChange={onChange} />
                                }
                            </Flex>
                        </Flex>

                        <Flex justifyContent={'end'} marginTop={10} gap={'size-50'}>
                            <ActionButton onPress={handleConfirm} isQuiet UNSAFE_style={{ color: '#1976D2', padding: 10 }}><Check size={22} color='#1976D2' /> {_tt('button_confirm')}</ActionButton>
                        </Flex>
                        <Divider size='M' UNSAFE_style={{ marginTop: 10 }} />
                    </DisclosurePanel>
                </Disclosure>
            </Accordion>
        );
    }

